import { memo, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  useDraggable,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineHint } from "../common/InlineHint";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  AddTeamCard,
  IncompleteTeamsBanner,
  PlayerChip,
  SectionHeading,
  StatPill,
} from "./TeamsBoardParts";
import { TeamCard, TeamRow, type SlotDropData } from "./TeamCard";
import {
  boardStats,
  playerEqual,
  type BoardId,
  type BoardPlayer,
  type BoardTeam,
} from "./teamsBoardModel";

/**
 * Past this many teams the board stops showing every team as a full card and
 * collapses the finished ones to rows, so the teams still needing work stay on
 * screen together rather than being scrolled past.
 */
const LARGE_LEAGUE_TEAMS = 4;

/**
 * Module constants, not literals in the render.
 *
 * useSensor memoizes on the options object's identity, so a fresh literal each
 * render produces a new sensor descriptor, which makes DndContext re-bind its
 * listeners. Pressing a chip selects it, and that re-render would then discard
 * the activation the same press had just begun — the drag never started.
 */
const POINTER_ACTIVATION = { activationConstraint: { distance: 4 } };

/** A rail chip wired as a dnd-kit drag source. */
function DraggableChipImpl({
  player,
  isSelected,
  disabled,
  onSelect,
}: {
  player: BoardPlayer;
  isSelected: boolean;
  /** An assignment is in flight — this chip can't start another. */
  disabled: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `player:${String(player.userId)}`,
    data: { userId: player.userId },
    disabled,
  });
  return (
    <PlayerChip
      player={player}
      isSelected={isSelected}
      isDragging={isDragging}
      disabled={disabled}
      onClick={onSelect}
      innerRef={setNodeRef}
      dragProps={{ ...listeners, ...attributes }}
    />
  );
}

/**
 * `unassigned` is rebuilt from scratch on every board recompute (see
 * `unassignedPlayers`), so comparing by value rather than reference is what
 * lets a refetch skip every chip but the one that actually changed.
 * `onSelect` is left out of the comparison: it toggles off `player.userId`
 * against the current `selectedUserId`, and `isSelected` — which IS
 * compared — already reflects any change to that id, so a stale-but-bailed
 * closure still toggles the right chip.
 */
const DraggableChip = memo(
  DraggableChipImpl,
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.disabled === next.disabled &&
    playerEqual(prev.player, next.player),
);

/** Stable empty default — a fresh `new Set()` on every render would defeat
 *  the memo comparators that key off these. */
const NO_PENDING: ReadonlySet<BoardId> = new Set();

export interface TeamsBoardProps {
  teams: BoardTeam[];
  players: BoardPlayer[];
  unassigned: BoardPlayer[];
  isBusy?: boolean;
  /**
   * Players per team, from the league. Required: there is no sane default —
   * guessing 2 shows a 3s league's two-player team as complete.
   */
  teamSize: number;
  /**
   * Players with a removal in flight. The cache updates optimistically, so
   * this only has to block a second removal of the *same* player — one
   * operator pulling a different player off another team has nothing to wait
   * on, and gating the whole board would just reintroduce the stall the
   * optimistic update was built to remove.
   */
  pendingRemoveIds?: ReadonlySet<BoardId>;
  /**
   * Players with an assignment in flight, the same way: placement is still a
   * request, not a local edit, so a player can't be dropped on a second team
   * before their first drop lands — but only for that one player. Everyone
   * else's chip stays fully interactive.
   */
  pendingAssignIds?: ReadonlySet<BoardId>;
  /** The caller already labels this section; the board drops its own <h2>. */
  hideHeading?: boolean;
  /**
   * Create League builds the shells itself. The league page still offers Add
   * Team for an extra empty container.
   */
  allowAddTeam?: boolean;
  /**
   * When set, the board is view-only and this sentence replaces the drag
   * instructions. Add, rename, delete, remove, and drop are all closed.
   */
  lockMessage?: string;
  onAddTeam: () => void;
  onRenameTeam: (teamId: BoardId, name: string) => void;
  onDeleteTeam: (team: BoardTeam) => void;
  /** Started team stroke/points: withdraw the whole team. Hidden otherwise. */
  onWithdrawTeam?: (team: BoardTeam) => void;
  onRemovePlayer: (teamId: BoardId, userId: BoardId) => void;
  onAssignPlayer: (teamId: BoardId, userId: BoardId) => void;
}

/**
 * The Teams board: team cards on the left, an Unassigned rail on the right.
 *
 * Assignment is drag-and-drop, with click-to-place as an equal path rather than
 * a fallback — select a player in the rail, then click a slot. That is what
 * makes the board usable by keyboard, on a long roster where dragging across a
 * scroll is awkward, and on a touch screen where precision is poor.
 */
export function TeamsBoard({
  teams,
  players,
  unassigned,
  isBusy = false,
  teamSize,
  pendingRemoveIds = NO_PENDING,
  pendingAssignIds = NO_PENDING,
  hideHeading = false,
  allowAddTeam = false,
  lockMessage,
  onAddTeam,
  onRenameTeam,
  onDeleteTeam,
  onWithdrawTeam,
  onRemovePlayer,
  onAssignPlayer,
}: TeamsBoardProps) {
  const [selectedUserId, setSelectedUserId] = useState<BoardId | null>(null);
  const [draggingId, setDraggingId] = useState<BoardId | null>(null);
  const [expandedTeamId, setExpandedTeamId] = useState<BoardId | null>(null);
  // One tree, not two behind CSS: each empty slot registers a dnd-kit droppable
  // keyed by team and index, so rendering both layouts would register every id
  // twice and leave which node receives a drop undefined.
  const isMobile = useIsMobile();

  const stats = useMemo(
    () => boardStats(players, teams, teamSize),
    [players, teams, teamSize],
  );
  const isLargeLeague = teams.length >= LARGE_LEAGUE_TEAMS;
  const needsAttention = useMemo(
    () => teams.filter((t) => !t.isComplete),
    [teams],
  );
  const completeTeams = useMemo(
    () => teams.filter((t) => t.isComplete),
    [teams],
  );
  const byId = useMemo(
    () => new Map(players.map((p) => [p.userId, p])),
    [players],
  );
  const selected =
    selectedUserId != null &&
    !pendingAssignIds.has(selectedUserId) &&
    !pendingRemoveIds.has(selectedUserId)
      ? byId.get(selectedUserId)
      : undefined;
  const dragging = draggingId != null ? byId.get(draggingId) : undefined;

  // Pointer covers mouse and touch through one path; the small distance
  // constraint keeps a tap-to-select from being swallowed as a drag.
  //
  // No KeyboardSensor, deliberately. Its activator calls preventDefault() on
  // Space and Enter, which suppresses the click those keys synthesize on the
  // rail chip — so a keyboard user could never select a player, and
  // click-to-place, the board's actual keyboard path, was unreachable. The
  // drag it offered instead could never land either: pointerWithin returns no
  // collisions without pointer coordinates, which a keyboard drag never has.
  // Restoring a keyboard drag means a coordinate-aware collision fallback and
  // a select path off Space/Enter; click-to-place already covers the need.
  const sensors = useSensors(useSensor(PointerSensor, POINTER_ACTIVATION));

  // Ids come off the drag payloads, never parsed back out of the dnd-kit id
  // string. The API's ids are opaque — "Qd9bRzV5", not a number — so decoding
  // them from a composite key silently dropped every drop.
  const activePlayerId = (e: DragStartEvent | DragEndEvent): BoardId | null => {
    const data = e.active.data.current as { userId?: BoardId } | undefined;
    return data?.userId ?? null;
  };

  const handleDragStart = (e: DragStartEvent) => {
    setDraggingId(activePlayerId(e));
    setSelectedUserId(null);
  };

  const locked = !!lockMessage;

  const handleDragEnd = (e: DragEndEvent) => {
    setDraggingId(null);
    const target = e.over?.data.current as SlotDropData | undefined;
    const userId = activePlayerId(e);
    if (
      locked ||
      !target ||
      userId == null ||
      pendingAssignIds.has(userId) ||
      pendingRemoveIds.has(userId)
    )
      return;
    onAssignPlayer(target.teamId, userId);
  };

  const placeSelected = (teamId: BoardId) => {
    if (
      locked ||
      selectedUserId == null ||
      pendingAssignIds.has(selectedUserId) ||
      pendingRemoveIds.has(selectedUserId)
    )
      return;
    onAssignPlayer(teamId, selectedUserId);
    setSelectedUserId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      // Collide on the pointer, not on the dragged rect. The overlay is
      // positioned from the pointer, so any transformed ancestor — a tab
      // panel's entrance animation, for one — offsets that rect and
      // rectIntersection stops matching while the drag still looks correct.
      collisionDetection={pointerWithin}
      // Re-measure droppables continuously rather than once at drag start:
      // this board lives inside scrollable tab content, and a scroll mid-drag
      // otherwise leaves every slot's rect stale.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggingId(null)}
    >
      <div className="@container flex w-full flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            {!hideHeading && (
              <h2 className="poppins text-xl font-bold tracking-tight text-text-primary">
                Teams
              </h2>
            )}
            <InlineHint className={hideHeading ? undefined : "mt-1"}>
              {lockMessage ??
                (teams.length === 0
                  ? allowAddTeam
                    ? "No teams yet — add a team, then drag players from Unassigned onto its slots."
                    : "Teams are created from the roster. Drag a player onto a slot, or click a player then click a slot."
                  : "Drag a player onto a slot, or click a player then click a slot to place them.")}
            </InlineHint>
          </div>
          {!locked && allowAddTeam && (
            <Button
              type="button"
              size="sm"
              onClick={onAddTeam}
              disabled={isBusy}
              className="rounded-lg"
            >
              <Plus data-icon="inline-start" />
              Add Team
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <StatPill>
            <b className="font-semibold tabular-nums text-text-primary">
              {stats.rosterCount}
            </b>{" "}
            Roster Players
          </StatPill>
          <StatPill>
            <b className="font-semibold tabular-nums text-text-primary">
              {stats.teamCount}
            </b>{" "}
            {stats.teamCount === 1 ? "Team" : "Teams"}
          </StatPill>
          <StatPill>
            <b className="font-semibold tabular-nums text-text-primary">
              {stats.unassignedCount}
            </b>{" "}
            Unassigned
          </StatPill>
        </div>

        {stats.needsPartner > 0 && !locked && (
          <IncompleteTeamsBanner
            needsPartner={stats.needsPartner}
            unassignedCount={stats.unassignedCount}
            rosterCount={stats.rosterCount}
            teamSize={teamSize}
          />
        )}

        {/* Side by side as soon as team cards are two-up. The wizard shell is
            about 1000px, so a 960px container query never fires there and the
            rail drops underneath, full width. A full-width chip is what the
            drag overlay copies, and that pill is wider than a team card. */}
        <div className="flex flex-col gap-6 @min-[720px]:flex-row @min-[720px]:items-start">
          <div className="min-w-0 flex-1">
            {teams.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border-[1.5px] border-dashed border-border bg-surface px-8 py-12 text-center">
                <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Plus className="size-6" aria-hidden />
                </span>
                <p className="poppins text-base font-semibold text-text-primary">
                  No teams yet
                </p>
                <p className="mulish max-w-[45ch] text-sm leading-relaxed text-text-muted">
                  {locked
                    ? "No teams have been built for this league."
                    : allowAddTeam
                      ? "Add a team to get started, then drag players from the Unassigned list onto its slots."
                      : "Add players on the Roster step. A team is created for every two players."}
                </p>
                {!locked && allowAddTeam && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={onAddTeam}
                    disabled={isBusy}
                  >
                    <Plus data-icon="inline-start" />
                    Add Team
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Below md a two-up grid can't hold a card, so teams read as
                    compact rows that expand one at a time. */}
                {isMobile ? (
                  <div className="flex flex-col gap-2">
                    {teams.map((team) => (
                      // No gap when expanded: the nested card drops its top
                      // border to sit flush against the row, so any space
                      // would leave an open-topped box floating below it.
                      // Collapsed, the card isn't rendered and gap is moot.
                      <div key={team.teamId} className="flex flex-col">
                        <TeamRow
                          team={team}
                          isExpanded={expandedTeamId === team.teamId}
                          onExpand={() =>
                            setExpandedTeamId(
                              expandedTeamId === team.teamId
                                ? null
                                : team.teamId,
                            )
                          }
                        />
                        {expandedTeamId === team.teamId && (
                          <TeamCard
                            nested
                            team={team}
                            teams={teams}
                            armedPlayerName={selected?.name}
                            pendingRemoveIds={pendingRemoveIds}
                            pendingAssignIds={pendingAssignIds}
                            readOnly={locked}
                            onRename={(n) => onRenameTeam(team.teamId, n)}
                            onDelete={() => onDeleteTeam(team)}
                            onWithdraw={
                              onWithdrawTeam
                                ? () => onWithdrawTeam(team)
                                : undefined
                            }
                            onRemovePlayer={(u) =>
                              onRemovePlayer(team.teamId, u)
                            }
                            onPlaceInSlot={() => placeSelected(team.teamId)}
                          />
                        )}
                      </div>
                    ))}
                    {!locked && allowAddTeam && (
                      <AddTeamCard onClick={onAddTeam} disabled={isBusy} />
                    )}
                  </div>
                ) : isLargeLeague ? (
                  // Complete teams collapse; incomplete ones stay open, since
                  // those are the ones the operator still has work to do on.
                  // Add Team is the header button here — a dashed tile at the
                  // end of a long collapsed list is a long way from the top.
                  <div className="flex flex-col gap-6">
                    {needsAttention.length > 0 && (
                      <section>
                        <SectionHeading
                          label="Needs attention"
                          count={needsAttention.length}
                        />
                        <div className="flex flex-col gap-4">
                          {needsAttention.map((team) => (
                            <TeamCard
                              key={team.teamId}
                              team={team}
                              teams={teams}
                              armedPlayerName={selected?.name}
                              pendingRemoveIds={pendingRemoveIds}
                              pendingAssignIds={pendingAssignIds}
                              readOnly={locked}
                              onRename={(n) => onRenameTeam(team.teamId, n)}
                              onDelete={() => onDeleteTeam(team)}
                            onWithdraw={
                              onWithdrawTeam
                                ? () => onWithdrawTeam(team)
                                : undefined
                            }
                              onRemovePlayer={(u) =>
                                onRemovePlayer(team.teamId, u)
                              }
                              onPlaceInSlot={() => placeSelected(team.teamId)}
                            />
                          ))}
                        </div>
                      </section>
                    )}
                    {completeTeams.length > 0 && (
                      <section>
                        <SectionHeading
                          label="Complete teams"
                          count={completeTeams.length}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          {completeTeams.map((team) => (
                            <div key={team.teamId} className="flex flex-col">
                              <TeamRow
                                team={team}
                                isExpanded={expandedTeamId === team.teamId}
                                onExpand={() =>
                                  setExpandedTeamId(
                                    expandedTeamId === team.teamId
                                      ? null
                                      : team.teamId,
                                  )
                                }
                              />
                              {expandedTeamId === team.teamId && (
                                <TeamCard
                                  nested
                                  team={team}
                                  teams={teams}
                                  armedPlayerName={selected?.name}
                                  pendingRemoveIds={pendingRemoveIds}
                                  pendingAssignIds={pendingAssignIds}
                                  readOnly={locked}
                                  onRename={(n) => onRenameTeam(team.teamId, n)}
                                  onDelete={() => onDeleteTeam(team)}
                            onWithdraw={
                              onWithdrawTeam
                                ? () => onWithdrawTeam(team)
                                : undefined
                            }
                                  onRemovePlayer={(u) =>
                                    onRemovePlayer(team.teamId, u)
                                  }
                                  onPlaceInSlot={() =>
                                    placeSelected(team.teamId)
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 @min-[720px]:grid-cols-2">
                    {teams.map((team) => (
                      <TeamCard
                        key={team.teamId}
                        team={team}
                        teams={teams}
                        armedPlayerName={selected?.name}
                        pendingRemoveIds={pendingRemoveIds}
                        pendingAssignIds={pendingAssignIds}
                        readOnly={locked}
                        onRename={(n) => onRenameTeam(team.teamId, n)}
                        onDelete={() => onDeleteTeam(team)}
                        onWithdraw={
                          onWithdrawTeam
                            ? () => onWithdrawTeam(team)
                            : undefined
                        }
                        onRemovePlayer={(u) => onRemovePlayer(team.teamId, u)}
                        onPlaceInSlot={() => placeSelected(team.teamId)}
                      />
                    ))}
                    {!locked && allowAddTeam && (
                      <AddTeamCard onClick={onAddTeam} disabled={isBusy} />
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <aside className="w-full shrink-0 @min-[720px]:w-56">
            <div className="rounded-2xl border border-border bg-surface p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="poppins text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
                  Unassigned
                </h3>
                <span className="mulish shrink-0 rounded-full bg-muted/60 px-2 py-0.5 text-xs font-semibold tabular-nums text-text-muted">
                  {unassigned.length}
                </span>
              </div>
              <InlineHint className="mb-4 min-w-0 items-start whitespace-normal text-xs">
                Full-time only — substitutes are on the Roster tab.
              </InlineHint>
              {unassigned.length === 0 ? (
                <p className="mulish flex items-center justify-center gap-1.5 rounded-xl bg-muted/60 px-3 py-4 text-center text-xs text-text-muted">
                  <Check className="size-3.5 shrink-0 text-good" aria-hidden />
                  Everyone has a team.
                </p>
              ) : (
                <div
                  className={cn(
                    "flex flex-col gap-2",
                    selected && "rounded-xl ring-1 ring-primary/20",
                  )}
                >
                  {unassigned.map((p) => (
                    <DraggableChip
                      key={p.userId}
                      player={p}
                      isSelected={selectedUserId === p.userId}
                      disabled={
                        locked ||
                        pendingAssignIds.has(p.userId) ||
                        pendingRemoveIds.has(p.userId)
                      }
                      onSelect={() =>
                        setSelectedUserId(
                          selectedUserId === p.userId ? null : p.userId,
                        )
                      }
                    />
                  ))}
                </div>
              )}
              {selected && (
                <p className="mulish mt-4 text-xs leading-relaxed text-primary">
                  Now click an empty slot to place {selected.name}.
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>

      <DragOverlay>
        {dragging ? (
          <div className="pointer-events-none">
            <PlayerChip player={dragging} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
